import { useState } from 'react';
import {
  Alert,
  Button,
  Card,
  CardContent,
  CloudUploadIcon,
  DeleteOutlineIcon,
  EditIcon,
  Field,
  FileDownloadOutlinedIcon,
  InsertDriveFileIcon,
  Skeleton,
  Stack,
  Text,
} from '@billda/design-system';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  Input,
} from '@billda/design-system';
import { PermissionGate } from '../../../../shared/components/gates/PermissionGate';
import { Permissions } from '@billda/permissions-ui';
import { useFeature, Features } from '@billda/features-ui';
import { getErrorMessage } from '@billda/shared-ui';
import { useQueryClient } from '@tanstack/react-query';
import ConfirmDialog from '../../../../shared/components/ui/ConfirmDialog';
import { fileDetailKeys, useFileDetail } from '../../hooks/useFileDetail';
import { useFileDownload } from '../../hooks/useFileDownload';
import { useDeleteFile } from '../../hooks/useDeleteFile';
import { useRenameFile } from '../../hooks/useFolderMutations';
import { getFileIcon } from './fileIcons';
import { formatFileSize } from './formatFileSize';
import { VersionHistorySection } from './VersionHistorySection';
import { VersionUploadArea } from './VersionUploadArea';

const dateFormatter = new Intl.DateTimeFormat('en-GB', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
});

const dateTimeFormatter = new Intl.DateTimeFormat('en-GB', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

function getContentTypeLabel(contentType: string): string {
  if (contentType === 'application/pdf') return 'PDF Document';
  if (contentType.startsWith('image/')) return 'Image';
  if (contentType.includes('spreadsheet') || contentType.includes('excel'))
    return 'Spreadsheet';
  if (contentType.includes('word') || contentType.includes('document'))
    return 'Document';
  return 'File';
}

function getEventIcon(eventType: string, className: string) {
  switch (eventType) {
    case 'FileCreated':
      return <CloudUploadIcon className={className} aria-hidden="true" />;
    case 'FileDownloaded':
      return (
        <FileDownloadOutlinedIcon className={className} aria-hidden="true" />
      );
    case 'FileDeleted':
      return <DeleteOutlineIcon className={className} aria-hidden="true" />;
    default:
      return <InsertDriveFileIcon className={className} aria-hidden="true" />;
  }
}

function getEventIconColorClass(eventType: string): string {
  switch (eventType) {
    case 'FileCreated':
      return 'text-content-success';
    case 'FileDownloaded':
      return 'text-content-accent';
    case 'FileDeleted':
      return 'text-content-error';
    default:
      return 'text-content-secondary';
  }
}

function getEventLabel(eventType: string): string {
  switch (eventType) {
    case 'FileCreated':
      return 'Uploaded this file';
    case 'FileDownloaded':
      return 'Downloaded this file';
    case 'FileDeleted':
      return 'Deleted this file';
    default:
      return eventType;
  }
}

interface FileDetailPanelProps {
  fileId: string;
  siteId: number;
  onClose: () => void;
}

export function FileDetailPanel({
  fileId,
  siteId,
  onClose,
}: FileDetailPanelProps) {
  const queryClient = useQueryClient();
  const { data, error } = useFileDetail(fileId);
  const download = useFileDownload();
  const deleteFile = useDeleteFile(siteId);
  const renameFile = useRenameFile();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [renameOpen, setRenameOpen] = useState(false);
  const [renameName, setRenameName] = useState('');
  const isEnhancedFileManager = useFeature(
    Features.Files.IsEnhancedFileManagerEnabled
  );

  const currentVersionNumber =
    data?.versions?.find(v => v.isCurrent)?.versionNumber ?? 1;

  const invalidateDetail = () => {
    queryClient.invalidateQueries({ queryKey: fileDetailKeys.detail(fileId) });
  };

  const openRename = () => {
    if (data) {
      setRenameName(data.name);
      setRenameOpen(true);
    }
  };

  const handleRename = () => {
    const trimmed = renameName.trim();
    if (!trimmed || trimmed === data?.name) {
      return;
    }
    renameFile.mutate(
      { fileId, name: trimmed },
      {
        onSuccess: () => {
          setRenameOpen(false);
          invalidateDetail();
        },
      }
    );
  };

  if (error && !data) {
    return (
      <div data-testid="file-detail-panel" className="p-6">
        <Alert severity="error">{getErrorMessage(error)}</Alert>
      </div>
    );
  }

  if (!data) {
    return (
      <div data-testid="file-detail-panel" className="p-6">
        <Stack spacing={2}>
          <Skeleton variant="rectangular" height={200} />
          <Skeleton variant="rectangular" height={120} />
        </Stack>
      </div>
    );
  }

  return (
    <div data-testid="file-detail-panel" className="flex h-full flex-col">
      <div className="flex-1 overflow-auto p-6">
        <Stack spacing={3}>
          {/* File summary card */}
          <Card>
            <CardContent className="pt-4">
              <Stack spacing={2}>
                {/* File icon + name */}
                <div className="flex items-start gap-3">
                  {getFileIcon(data.contentType, 40)}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-start gap-1">
                      <Text
                        variant="body1"
                        className="flex-1 font-semibold break-words"
                      >
                        {data.name}
                      </Text>
                      <PermissionGate permission={Permissions.Files.Write}>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="-mt-1 h-7 w-7 p-0"
                          aria-label="Rename file"
                          onClick={openRename}
                          data-testid="rename-file-button"
                        >
                          <EditIcon className="size-4.5" aria-hidden="true" />
                        </Button>
                      </PermissionGate>
                    </div>
                    <Text variant="caption" color="secondary">
                      {getContentTypeLabel(data.contentType)} &middot;{' '}
                      {formatFileSize(data.fileSizeBytes)}
                    </Text>
                  </div>
                </div>

                {/* Metadata rows */}
                <Stack spacing={1}>
                  <div className="flex justify-between">
                    <Text variant="body2" color="secondary">
                      Uploaded
                    </Text>
                    <Text variant="body2" className="font-medium">
                      {dateFormatter.format(new Date(data.uploadedAt))}
                    </Text>
                  </div>

                  <div className="flex justify-between">
                    <Text variant="body2" color="secondary">
                      Uploaded by
                    </Text>
                    <Text variant="body2" className="font-medium">
                      {data.uploadedByName}
                    </Text>
                  </div>
                </Stack>

                {/* Site context */}
                <div className="border-t border-border-default pt-2">
                  <Text variant="caption" color="secondary">
                    Site
                  </Text>
                  <Text variant="body2" className="font-medium">
                    {data.siteName}
                  </Text>
                </div>
              </Stack>
            </CardContent>
          </Card>

          {/* Version History */}
          {isEnhancedFileManager &&
            data.versions &&
            data.versions.length > 0 && (
              <div>
                <Text variant="h6" className="mb-4">
                  Version History
                </Text>
                <VersionHistorySection
                  versions={data.versions}
                  fileId={fileId}
                  onVersionChange={invalidateDetail}
                />
              </div>
            )}

          {/* Upload new version */}
          {isEnhancedFileManager && (
            <PermissionGate permission={Permissions.Files.Write}>
              <VersionUploadArea
                fileId={fileId}
                contentType={data.contentType}
                onUploadComplete={invalidateDetail}
              />
            </PermissionGate>
          )}

          {/* Activity timeline */}
          {data.events.length > 0 && (
            <div>
              <Text variant="h6" className="mb-4">
                Activity
              </Text>
              <div className="overflow-hidden rounded-xl border border-border-default bg-surface-default">
                {data.events.map((event, index) => (
                  <div key={index}>
                    <div
                      data-testid="file-event"
                      className="flex gap-4 px-4 py-4 transition-colors hover:bg-surface-raised"
                    >
                      {/* Event icon */}
                      <div
                        className={`flex shrink-0 items-center justify-center pt-0.5 ${getEventIconColorClass(event.eventType)}`}
                      >
                        {getEventIcon(event.eventType, 'size-5')}
                      </div>

                      {/* Event content */}
                      <div className="min-w-0 flex-1">
                        <Text
                          variant="body2"
                          className="font-semibold text-content-primary"
                        >
                          {getEventLabel(event.eventType)}
                        </Text>
                        <Text variant="caption" color="disabled">
                          {event.userName} &middot;{' '}
                          {dateTimeFormatter.format(new Date(event.timestamp))}
                        </Text>
                      </div>
                    </div>
                    {index < data.events.length - 1 && (
                      <div className="mx-4 border-b border-border-default" />
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </Stack>
      </div>

      {/* Fixed footer — actions */}
      <div className="border-t border-border-default bg-surface-default p-4">
        <Stack spacing={1.5}>
          <Button
            variant="neutral"
            loading={download.isPending}
            onClick={() => download.mutate(fileId)}
            disabled={download.isPending}
            fullWidth
            data-testid="download-file-button"
          >
            {download.isPending
              ? 'Downloading…'
              : isEnhancedFileManager
                ? `Download (v${currentVersionNumber})`
                : 'Download'}
          </Button>
          <PermissionGate permission={Permissions.Files.Write}>
            <Button
              variant="outlineDanger"
              onClick={() => setConfirmOpen(true)}
              disabled={deleteFile.isPending}
              fullWidth
              data-testid="delete-file-button"
            >
              <DeleteOutlineIcon className="size-4" aria-hidden="true" />
              Delete
            </Button>
          </PermissionGate>
        </Stack>
      </div>

      <ConfirmDialog
        open={confirmOpen}
        title="Delete file?"
        message="This file will be moved to the recycle bin."
        confirmText="Delete"
        onConfirm={() => {
          setConfirmOpen(false);
          deleteFile.mutate(fileId, { onSuccess: () => onClose() });
        }}
        onCancel={() => setConfirmOpen(false)}
      />

      <Dialog
        open={renameOpen}
        onOpenChange={open => !open && setRenameOpen(false)}
      >
        <DialogContent data-testid="rename-file-dialog">
          <DialogHeader>Rename file</DialogHeader>
          <DialogBody>
            <Field label="File name" className="mt-2">
              <Input
                autoFocus
                value={renameName}
                onChange={e => setRenameName(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    handleRename();
                  }
                }}
                maxLength={255}
                data-testid="rename-file-input"
              />
            </Field>
          </DialogBody>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRenameOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="neutral"
              onClick={handleRename}
              disabled={
                !renameName.trim() ||
                renameName.trim() === data?.name ||
                renameFile.isPending
              }
              data-testid="rename-file-confirm-button"
            >
              Rename
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
